import { useState } from "react";
import {
  Terminal,
  Lock,
  Megaphone,
  Search,
  Wrench,
  ShieldCheck,
  Undo2,
  Check,
  PlayCircle,
  Circle,
  SkipForward,
  RotateCcw,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { toast } from "sonner";
import ComingSoon from "./ComingSoon";

const LIVE_EXEC_TOOLTIP =
  "CRE output is advisory. Runbook steps require human validation and are not executed by CRE.";

const CATEGORIES = [
  {
    key: "business_containment",
    label: "1 · Business containment",
    overline: "Containment",
    tone: "border-amber-500/30 text-amber-300 bg-amber-500/[0.04]",
    accent: "text-amber-300",
    icon: Megaphone,
  },
  {
    key: "technical_investigation",
    label: "2 · Technical investigation",
    overline: "Investigation",
    tone: "border-sky-500/30 text-sky-300 bg-sky-500/[0.04]",
    accent: "text-sky-300",
    icon: Search,
  },
  {
    key: "technical_remediation",
    label: "3 · Technical remediation",
    overline: "Remediation",
    tone: "border-emerald-500/30 text-emerald-300 bg-emerald-500/[0.04]",
    accent: "text-emerald-300",
    icon: Wrench,
  },
  {
    key: "validation_checks",
    label: "4 · Validation checks",
    overline: "Validation",
    tone: "border-violet-500/30 text-violet-300 bg-violet-500/[0.04]",
    accent: "text-violet-300",
    icon: ShieldCheck,
  },
  {
    key: "rollback_safety",
    label: "5 · Rollback / safety plan",
    overline: "Safety",
    tone: "border-rose-500/30 text-rose-300 bg-rose-500/[0.04]",
    accent: "text-rose-300",
    icon: Undo2,
  },
];

function highlightYaml(text) {
  if (!text) return null;
  return text.split("\n").map((line, i) => {
    if (line.startsWith("#") || line.startsWith("  #")) {
      return (
        <div key={i}>
          <span className="yaml-comment">{line}</span>
        </div>
      );
    }
    const keyMatch = line.match(/^(\s*-?\s*)([A-Za-z_]+):(.*)$/);
    if (keyMatch) {
      const [, indent, key, rest] = keyMatch;
      return (
        <div key={i}>
          <span>{indent}</span>
          <span className="yaml-key">{key}</span>
          <span>:</span>
          <span>{highlightValue(rest)}</span>
        </div>
      );
    }
    return <div key={i}>{line}</div>;
  });
}

function highlightValue(rest) {
  if (!rest) return rest;
  const strMatch = rest.match(/^(\s*)"(.*)"$/);
  if (strMatch) {
    return (
      <>
        {strMatch[1]}
        <span className="yaml-str">"{strMatch[2]}"</span>
      </>
    );
  }
  const numMatch = rest.match(/^(\s*)(-?\d+(?:\.\d+)?)$/);
  if (numMatch) {
    return (
      <>
        {numMatch[1]}
        <span className="yaml-num">{numMatch[2]}</span>
      </>
    );
  }
  return rest;
}

export default function RunbookPanel({
  runbook,
  incidentId,
  acks,
  onIncidentUpdate,
  readOnly = false,
}) {
  if (!runbook) return null;

  const ackByStep = (acks || []).reduce((m, a) => {
    m[a.step_id] = a;
    return m;
  }, {});
  const total = (runbook.steps || []).length;
  const doneCount = (acks || []).filter((a) => a.state === "done").length;
  const inProgressCount = (acks || []).filter(
    (a) => a.state === "in_progress"
  ).length;

  return (
    <div className="cre-surface rounded-sm" data-testid="runbook-panel">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 p-5 border-b border-white/10 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-sm border border-white/15 bg-black/60 flex items-center justify-center">
            <Terminal className="h-4 w-4 text-emerald-400" strokeWidth={1.5} />
          </div>
          <div>
            <div className="cre-overline">Generated runbook</div>
            <h3 className="font-heading text-lg font-bold tracking-tight">
              {runbook.title}
            </h3>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Badge
            variant="outline"
            data-testid="runbook-source"
            className="rounded-sm font-mono text-[10px] border-white/15 text-zinc-400"
          >
            source · {runbook.source || "fallback"}
          </Badge>
          <Badge
            variant="outline"
            className="rounded-sm font-mono text-[10px] border-white/15 text-zinc-400"
          >
            {runbook.steps?.length || 0} steps
          </Badge>
          {!readOnly && total > 0 ? (
            <Badge
              variant="outline"
              data-testid="runbook-progress"
              className={`rounded-sm font-mono text-[10px] ${
                doneCount === total
                  ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/5"
                  : doneCount + inProgressCount > 0
                  ? "border-blue-500/40 text-blue-300 bg-blue-500/5"
                  : "border-white/15 text-zinc-400"
              }`}
            >
              progress · {doneCount}/{total} done
              {inProgressCount > 0 ? ` · ${inProgressCount} live` : ""}
            </Badge>
          ) : null}
          <ComingSoon label="Ansible export" variant="ghost" />
          <span
            data-testid="live-execution-locked"
            title={LIVE_EXEC_TOOLTIP}
            className="font-mono text-[10px] tracking-[0.2em] uppercase rounded-sm px-1.5 py-0.5 border border-blue-500/30 text-blue-300 bg-blue-500/5 inline-flex items-center"
          >
            <Lock className="inline h-3 w-3 mr-1 -mt-0.5" strokeWidth={1.75} />
            Human validation required
          </span>
        </div>
      </div>

      <div className="px-5 -mt-2 pb-3">
        <p
          data-testid="live-execution-helper"
          className="text-[11px] font-mono text-zinc-500 leading-relaxed"
        >
          {LIVE_EXEC_TOOLTIP}
        </p>
      </div>

      {/* Preconditions */}
      <div className="px-5 pb-4">
        <div className="cre-overline mb-2">Preconditions</div>
        <ul className="space-y-1 text-xs text-zinc-300">
          {(runbook.preconditions || []).map((p, i) => (
            <li
              key={`${p.check}-${i}`}
              data-testid={`runbook-precondition-${i}`}
              className="flex gap-2"
            >
              <span className="text-blue-400 font-mono">?</span>
              <span>
                {p.check}
                <span className="block text-zinc-500 font-mono">
                  expected: {p.expected}
                </span>
              </span>
            </li>
          ))}
        </ul>
      </div>

      {/* Categorized sections */}
      <div className="px-5 pb-2 space-y-4">
        {CATEGORIES.map((cat) => {
          const steps = (runbook.steps || []).filter(
            (s) => s.category === cat.key
          );
          if (!steps.length) return null;
          const Icon = cat.icon;
          return (
            <section
              key={cat.key}
              data-testid={`runbook-section-${cat.key}`}
              className={`border rounded-sm p-4 ${cat.tone}`}
            >
              <div className="flex items-center gap-2 mb-3">
                <Icon className={`h-4 w-4 ${cat.accent}`} strokeWidth={1.75} />
                <span className={`cre-overline ${cat.accent}`}>
                  {cat.overline}
                </span>
                <span className="font-heading text-sm font-bold tracking-tight text-zinc-100">
                  {cat.label.split(" · ")[1]}
                </span>
                <span className="ml-auto text-[10px] font-mono text-zinc-500">
                  {steps.length} {steps.length === 1 ? "step" : "steps"}
                </span>
              </div>
              <ol className="space-y-3">
                {steps.map((st) => (
                  <RunbookStepRow
                    key={st.step_id}
                    step={st}
                    ack={ackByStep[st.step_id]}
                    incidentId={incidentId}
                    readOnly={readOnly}
                    onIncidentUpdate={onIncidentUpdate}
                  />
                ))}
              </ol>
            </section>
          );
        })}
      </div>

      {/* Top-level rollback principles */}
      <div className="px-5 pb-4">
        <div className="cre-overline mb-2">Rollback principles</div>
        <ul className="space-y-1 text-xs text-zinc-300">
          {(runbook.rollback_plan || []).map((r, i) => (
            <li key={`${r}-${i}`} className="flex gap-2">
              <span className="text-rose-400 font-mono">↺</span>
              <span>{r}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* YAML preview */}
      <div className="p-5 pt-0">
        <div className="cre-overline mb-2">YAML preview</div>
        <pre
          data-testid="runbook-yaml"
          className="cre-terminal rounded-sm p-4 overflow-x-auto max-h-[420px] text-[11px]"
        >
          {highlightYaml(runbook.yaml_preview)}
        </pre>
        <p className="mt-2 text-[11px] font-mono text-amber-400/80">
          * Advisory output. CRE does not execute runbook steps — validate with the appropriate technical owner.
        </p>
      </div>
    </div>
  );
}


const STATE_TONE = {
  pending: "border-white/10 text-zinc-400 bg-black/20",
  in_progress: "border-blue-500/40 text-blue-300 bg-blue-500/[0.06]",
  done: "border-emerald-500/40 text-emerald-300 bg-emerald-500/[0.06]",
  skipped: "border-zinc-600 text-zinc-500 bg-zinc-900/40",
};

const STATE_ICON = {
  pending: Circle,
  in_progress: PlayCircle,
  done: Check,
  skipped: SkipForward,
};

// Click cycle: pending → in_progress → done → pending.
// Skipped is reachable via long-press / secondary action; for now it's the
// "alt" toggle accessible via shift-click for simplicity.
const NEXT_STATE = {
  pending: "in_progress",
  in_progress: "done",
  done: "pending",
  skipped: "pending",
};

function RunbookStepRow({ step, ack, incidentId, readOnly, onIncidentUpdate }) {
  const [busy, setBusy] = useState(false);
  const state = ack?.state || "pending";
  const Icon = STATE_ICON[state] || Circle;

  const sendAck = async (nextState) => {
    if (!incidentId || readOnly) return;
    setBusy(true);
    try {
      const inc = await api.ackRunbookStep(incidentId, step.step_id, {
        step_id: step.step_id,
        state: nextState,
        actor: "On-call Reviewer",
        note: "",
      });
      onIncidentUpdate?.(inc);
      const verb = {
        pending: "reset",
        in_progress: "started",
        done: "marked done",
        skipped: "skipped",
      }[nextState];
      toast.success(`Step ${step.step_id} ${verb}`);
    } catch (e) {
      toast.error("Could not update step");
    } finally {
      setBusy(false);
    }
  };

  const onPrimary = (e) => {
    if (e.shiftKey) {
      sendAck("skipped");
    } else {
      sendAck(NEXT_STATE[state]);
    }
  };

  const onReset = (e) => {
    e.stopPropagation();
    sendAck("pending");
  };

  return (
    <li
      data-testid={`runbook-step-${step.step_id}`}
      data-state={state}
      className={`border rounded-sm p-3 transition-colors ${STATE_TONE[state]}`}
    >
      <div className="flex items-start gap-3">
        <span className="font-mono text-[11px] text-zinc-500 mt-0.5 shrink-0">
          {String(step.step_id).padStart(2, "0")}.
        </span>
        <div className="flex-1 min-w-0">
          <div
            className={`text-sm leading-snug ${
              state === "done"
                ? "text-zinc-400 line-through decoration-zinc-700"
                : state === "skipped"
                ? "text-zinc-500 line-through decoration-zinc-700"
                : "text-zinc-100"
            }`}
          >
            {step.action}
          </div>
          <div className="mt-1.5 flex items-center gap-2 flex-wrap text-[10px] font-mono">
            <Badge
              variant="outline"
              className="rounded-sm font-mono text-[10px] border-white/15 text-zinc-300"
            >
              owner · {step.owner_role || "SRE"}
            </Badge>
            {step.timeout_seconds ? (
              <span className="text-zinc-500">
                timeout {step.timeout_seconds}s
              </span>
            ) : null}
            {ack ? (
              <span
                data-testid={`runbook-step-${step.step_id}-ack-meta`}
                className="text-zinc-500"
              >
                · {state.replace("_", " ")} by {ack.actor || "—"} ·{" "}
                {new Date(ack.updated_at).toLocaleTimeString()}
              </span>
            ) : null}
          </div>
          {step.references?.length ? (
            <div className="mt-2 flex flex-wrap gap-1">
              {step.references.map((r, i) => (
                <span
                  key={`${step.step_id}-ref-${r}-${i}`}
                  className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm border border-white/10 text-zinc-400 bg-white/[0.02]"
                >
                  {r}
                </span>
              ))}
            </div>
          ) : null}
          {step.commands_examples?.length ? (
            <div className="mt-2 cre-terminal rounded-sm p-2 text-[11px] space-y-0.5">
              {step.commands_examples.map((c, i) => (
                <div key={`${step.step_id}-cmd-${i}`} className="flex gap-2 leading-relaxed">
                  <span className="text-emerald-400 shrink-0">$</span>
                  <span className="text-zinc-200 break-all">{c}</span>
                </div>
              ))}
            </div>
          ) : null}
          {step.success_criteria ? (
            <div className="mt-2 text-[11px] text-zinc-400">
              <span className="text-emerald-400 font-mono">✓ success</span>{" "}
              <span className="text-zinc-300">{step.success_criteria}</span>
            </div>
          ) : null}
          {step.rollback ? (
            <div className="mt-1 text-[11px] text-zinc-400">
              <span className="text-rose-400 font-mono">↺ rollback</span>{" "}
              <span className="text-zinc-300">{step.rollback}</span>
            </div>
          ) : null}
        </div>

        {/* Ack toggle */}
        {!readOnly ? (
          <div className="flex flex-col items-stretch gap-1 shrink-0">
            <button
              onClick={onPrimary}
              disabled={busy}
              data-testid={`runbook-step-${step.step_id}-toggle`}
              data-state={state}
              title={
                state === "pending"
                  ? "Click to start · shift-click to skip"
                  : state === "in_progress"
                  ? "Click to mark done · shift-click to skip"
                  : state === "done"
                  ? "Click to reset to pending"
                  : "Click to reset to pending"
              }
              className={`inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest px-2 py-1 rounded-sm border transition-colors disabled:opacity-50 ${STATE_TONE[state]}`}
            >
              <Icon className="h-3 w-3" strokeWidth={2} />
              {state === "in_progress" ? "in progress" : state}
            </button>
            {state !== "pending" ? (
              <button
                onClick={onReset}
                disabled={busy}
                data-testid={`runbook-step-${step.step_id}-reset`}
                title="Reset to pending"
                className="inline-flex items-center justify-center text-[10px] font-mono text-zinc-500 hover:text-zinc-300 px-2 py-0.5 rounded-sm border border-white/10 hover:border-white/25 transition-colors"
              >
                <RotateCcw className="h-3 w-3 mr-1" strokeWidth={1.75} /> reset
              </button>
            ) : null}
          </div>
        ) : ack ? (
          <Badge
            variant="outline"
            data-testid={`runbook-step-${step.step_id}-state-readonly`}
            className={`rounded-sm font-mono text-[10px] uppercase ${STATE_TONE[state]}`}
          >
            {state.replace("_", " ")}
          </Badge>
        ) : null}
      </div>
    </li>
  );
}
